import {
  REMOTE_SOURCE,
  SHELL_SOURCE,
  type AdapterConfig,
  type AnyContractMessage,
  type ConversationDataPayload,
  type RemoteToShellMessage,
  type ShellToRemoteMessage,
} from './types';

interface ContractEnvelope {
  source: string;
  type: string;
  version: number;
  payload: unknown;
  requestId?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return isRecord(value) && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isNonEmptyString(value: unknown): value is string {
  return isString(value) && value.trim().length > 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isStringOrNull(value: unknown): value is string | null {
  return value === null || isString(value);
}

function hasValidRequestId(message: Record<string, unknown>): boolean {
  if (!('requestId' in message)) {
    return true;
  }

  return message.requestId === undefined || isString(message.requestId);
}

function isEnvelopeWithSource(
  data: unknown,
  source: string,
): data is ContractEnvelope {
  return (
    isRecord(data) &&
    data.source === source &&
    isString(data.type) &&
    isFiniteNumber(data.version) &&
    'payload' in data &&
    hasValidRequestId(data)
  );
}

function isStringRecord(value: unknown): value is Record<string, string> {
  if (!isRecord(value)) {
    return false;
  }

  return Object.values(value).every(isString);
}

function isConversationDataPayload(
  payload: unknown,
): payload is ConversationDataPayload {
  if (!isRecord(payload)) {
    return false;
  }

  if (
    !isString(payload.id) ||
    !isString(payload.name) ||
    !isString(payload.currentLeafId) ||
    !Array.isArray(payload.messages)
  ) {
    return false;
  }

  return payload.messages.every((message) => {
    if (!isRecord(message)) {
      return false;
    }

    return (
      isString(message.uuid) &&
      (message.parentUuid === null || isString(message.parentUuid)) &&
      (message.sender === 'human' || message.sender === 'assistant') &&
      isString(message.text) &&
      isStringOrNull(message.createdAt)
    );
  });
}

function isHandshakePayload(payload: unknown): boolean {
  if (!isRecord(payload) || !Array.isArray(payload.supportedVersions)) {
    return false;
  }

  return payload.supportedVersions.every(isFiniteNumber);
}

function isHandshakeAckPayload(payload: unknown): boolean {
  if (!isRecord(payload) || !isFiniteNumber(payload.selectedVersion)) {
    return false;
  }

  if (
    payload.platform !== undefined &&
    payload.platform !== 'claude' &&
    payload.platform !== 'chatgpt'
  ) {
    return false;
  }

  if (payload.extensionVersion !== undefined && !isString(payload.extensionVersion)) {
    return false;
  }

  return true;
}

function isHandshakeRejectPayload(payload: unknown): boolean {
  return (
    isRecord(payload) &&
    isFiniteNumber(payload.shellVersion) &&
    isFiniteNumber(payload.minSupported)
  );
}

function isCurrentLeafPayload(payload: unknown): boolean {
  return isRecord(payload) && isString(payload.leafId);
}

function isAuthStatePayload(payload: unknown): boolean {
  if (!isRecord(payload) || typeof payload.isAuthenticated !== 'boolean') {
    return false;
  }

  const userValid =
    payload.user === null || isRecord(payload.user);
  if (!userValid) {
    return false;
  }

  if (payload.subscription === null) {
    return true;
  }

  if (!isRecord(payload.subscription)) {
    return false;
  }

  return (
    typeof payload.subscription.isPro === 'boolean' &&
    isStringOrNull(payload.subscription.status) &&
    isStringOrNull(payload.subscription.trialEndsAt) &&
    isStringOrNull(payload.subscription.currentPeriodEnd)
  );
}

function isAuthTokenPayload(payload: unknown): boolean {
  return isRecord(payload) && isString(payload.accessToken);
}

function isNavigateResultPayload(payload: unknown): boolean {
  return (
    isRecord(payload) &&
    typeof payload.success === 'boolean' &&
    (payload.error === undefined || isString(payload.error))
  );
}

function isConfigAckPayload(payload: unknown): boolean {
  return isRecord(payload) && typeof payload.applied === 'boolean';
}

function isRefreshConversationResultPayload(payload: unknown): boolean {
  return (
    isRecord(payload) &&
    typeof payload.success === 'boolean' &&
    (payload.error === undefined || isString(payload.error))
  );
}

function isEmptyObjectPayload(payload: unknown): boolean {
  return isRecord(payload);
}

function isNavigateToNodePayload(payload: unknown): boolean {
  return isRecord(payload) && isString(payload.nodeId);
}

function isInjectPromptPayload(payload: unknown): boolean {
  if (!isRecord(payload) || !isString(payload.text)) {
    return false;
  }

  return payload.variables === undefined || isStringRecord(payload.variables);
}

export function isAdapterConfigPayload(payload: unknown): payload is AdapterConfig {
  if (!isPlainRecord(payload)) {
    return false;
  }

  if (
    (payload.platform !== 'claude' && payload.platform !== 'chatgpt') ||
    !isNonEmptyString(payload.version)
  ) {
    return false;
  }

  if (!isPlainRecord(payload.selectors) || !isPlainRecord(payload.endpoints)) {
    return false;
  }

  const hasRequiredSelectors =
    isNonEmptyString(payload.selectors.chatInput) &&
    isNonEmptyString(payload.selectors.messageContainer) &&
    isNonEmptyString(payload.selectors.messageIdAttribute);
  if (!hasRequiredSelectors) {
    return false;
  }

  const hasRequiredEndpoints =
    isNonEmptyString(payload.endpoints.conversationGet) &&
    isNonEmptyString(payload.endpoints.branchSwitch);
  if (!hasRequiredEndpoints) {
    return false;
  }

  const selectorsAreStringMap = Object.values(payload.selectors).every(
    isNonEmptyString,
  );
  if (!selectorsAreStringMap) {
    return false;
  }

  const endpointsAreStringMap = Object.values(payload.endpoints).every(
    isNonEmptyString,
  );
  if (!endpointsAreStringMap) {
    return false;
  }

  if (
    'parsing' in payload &&
    payload.parsing !== undefined &&
    !isPlainRecord(payload.parsing)
  ) {
    return false;
  }

  return true;
}

function isShellMessagePayloadValid(
  type: ShellToRemoteMessage['type'],
  payload: unknown,
): boolean {
  switch (type) {
    case 'HANDSHAKE_ACK':
      return isHandshakeAckPayload(payload);
    case 'HANDSHAKE_REJECT':
      return isHandshakeRejectPayload(payload);
    case 'CONVERSATION_DATA':
      return isConversationDataPayload(payload);
    case 'CURRENT_LEAF_UPDATED':
      return isCurrentLeafPayload(payload);
    case 'AUTH_STATE':
      return isAuthStatePayload(payload);
    case 'AUTH_TOKEN':
      return isAuthTokenPayload(payload);
    case 'NAVIGATE_RESULT':
    case 'PROMPT_INJECT_RESULT':
      return isNavigateResultPayload(payload);
    case 'CONFIG_ACK':
      return isConfigAckPayload(payload);
    case 'REFRESH_CONVERSATION_RESULT':
      return isRefreshConversationResultPayload(payload);
    default:
      return false;
  }
}

function isRemoteMessagePayloadValid(
  type: RemoteToShellMessage['type'],
  payload: unknown,
): boolean {
  switch (type) {
    case 'HANDSHAKE':
      return isHandshakePayload(payload);
    case 'READY':
    case 'AUTH_SIGN_IN':
    case 'AUTH_SIGN_OUT':
    case 'AUTH_TOKEN_REQUEST':
    case 'REFRESH_CONVERSATION':
      return isEmptyObjectPayload(payload);
    case 'NAVIGATE_TO_NODE':
      return isNavigateToNodePayload(payload);
    case 'INJECT_PROMPT':
      return isInjectPromptPayload(payload);
    case 'ADAPTER_CONFIG':
      return isAdapterConfigPayload(payload);
    default:
      return false;
  }
}

export function isShellToRemoteMessage(data: unknown): data is ShellToRemoteMessage {
  if (!isEnvelopeWithSource(data, SHELL_SOURCE)) {
    return false;
  }

  if (!isShellMessageType(data.type)) {
    return false;
  }

  return isShellMessagePayloadValid(data.type, data.payload);
}

export function isRemoteToShellMessage(data: unknown): data is RemoteToShellMessage {
  if (!isEnvelopeWithSource(data, REMOTE_SOURCE)) {
    return false;
  }

  if (!isRemoteMessageType(data.type)) {
    return false;
  }

  return isRemoteMessagePayloadValid(data.type, data.payload);
}

export function isAnyContractMessage(data: unknown): data is AnyContractMessage {
  return isShellToRemoteMessage(data) || isRemoteToShellMessage(data);
}

function isShellMessageType(type: string): type is ShellToRemoteMessage['type'] {
  return (
    type === 'HANDSHAKE_ACK' ||
    type === 'HANDSHAKE_REJECT' ||
    type === 'CONVERSATION_DATA' ||
    type === 'CURRENT_LEAF_UPDATED' ||
    type === 'AUTH_STATE' ||
    type === 'AUTH_TOKEN' ||
    type === 'NAVIGATE_RESULT' ||
    type === 'PROMPT_INJECT_RESULT' ||
    type === 'CONFIG_ACK' ||
    type === 'REFRESH_CONVERSATION_RESULT'
  );
}

function isRemoteMessageType(type: string): type is RemoteToShellMessage['type'] {
  return (
    type === 'HANDSHAKE' ||
    type === 'READY' ||
    type === 'NAVIGATE_TO_NODE' ||
    type === 'INJECT_PROMPT' ||
    type === 'AUTH_SIGN_IN' ||
    type === 'AUTH_SIGN_OUT' ||
    type === 'AUTH_TOKEN_REQUEST' ||
    type === 'ADAPTER_CONFIG' ||
    type === 'REFRESH_CONVERSATION'
  );
}
